import { ticketStatusSchema } from '@nbh/shared';
import type { TicketAuditEntry } from '@nbh/shared';

/**
 * What counts as a lifecycle event, and how one reads — shared by the History tab and the
 * chat thread's system rows (D5 of the chat brief), so there is one definition of both.
 * Two lists would drift the way two role sets did.
 */

/**
 * Actions that are part of the lifecycle rather than commentary.
 *
 * Every status name, plus the three non-status actions the server writes: `CREATE` for the
 * opening row, `TRANSITION` for a super-admin status override, and `ASSIGN` for a move
 * between people. Nothing else writes to this table yet — the filter exists because
 * attachments and chat are the obvious next writers, and a trail that drowns a rejection
 * in photo uploads has lost the thing R9 asked it to keep.
 *
 * `ASSIGN` was missing, and rows of it have existed in production data since the root tier
 * gained its edit form — so reassignments were filed away under "All events" while the
 * default filter, the one a manager actually reads, showed the job changing hands never.
 */
export const LIFECYCLE_ACTIONS = new Set<string>([
  ...ticketStatusSchema.options,
  'CREATE',
  'TRANSITION',
  'ASSIGN',
  // A priority change resetting the deadlines (Q-PRIORITY-EDIT). In the default view
  // because it explains the lifecycle around it — an escalation firing minutes after
  // a week-old job was promoted reads as a bug until this row says why.
  'REPRIORITISE',
  // The two halves of a pause (D10, D52). `HOLD` and `RESUME` are their own actions
  // rather than status names, and a thread that showed the hold but not the resume
  // would leave a technician thinking the job was still parked.
  'HOLD',
  'RESUME',
]);

type Translate = (key: string, values?: Record<string, string>) => string;

/**
 * Who acted.
 *
 * A null actor is not one thing. The escalation sweep genuinely has no user, and an
 * account hard-deleted under D25 nulls the row's actor while leaving the row — so a
 * system row says "System" and a human one says the work was done by someone since gone,
 * rather than both reading as a blank.
 */
export function actorName(entry: TicketAuditEntry, t: Translate): string {
  if (entry.actorName) return entry.actorName;

  return entry.action === 'ESCALATED' ? t('audit.systemActor') : t('detail.formerStaff');
}

/**
 * What they did, as a phrase.
 *
 * A rejection and an acknowledgement are both `IN_PROGRESS` on the wire and read as
 * completely different acts to a person, so the phrase is chosen from where the row came
 * from as well as where it went.
 *
 * An action with no copy falls back to the raw value rather than rendering an empty line
 * — an unfamiliar row is still evidence that something happened, and a blank is not.
 */
export function actionPhrase(entry: TicketAuditEntry, t: Translate): string {
  if (entry.action === 'IN_PROGRESS' && entry.fromStatus === 'RESOLVED') {
    return t('audit.phrase.rejected');
  }

  if (entry.action === 'ASSIGN') return assignPhrase(entry, t);

  const key = AUDIT_PHRASE_KEY[entry.action];
  return key ? t(key) : entry.action;
}

/**
 * A job changing hands, in four shapes.
 *
 * Both names, one name, or neither, and the copy has to read in every case. Neither is not
 * hypothetical: every `ASSIGN` row written before D38 carries no from and no to, because
 * the columns did not exist and the identities lived inside a prose sentence naming only
 * the actor. Those rows are real, they are in production data, and they used to render as
 * the literal string "ASSIGN". Saying "moved this job to someone else" is honest about what
 * was recorded; inventing a name from today's assignee would not be.
 *
 * A missing name on a row that has the other one means a hard-deleted account (D25), which
 * reads the same way to the person looking at it — the job came from somebody who is gone.
 */
function assignPhrase(entry: TicketAuditEntry, t: Translate): string {
  const from = entry.fromAssigneeName;
  const to = entry.toAssigneeName;

  if (from && to) return t('audit.phrase.movedBetween', { from, to });
  if (to) return t('audit.phrase.movedTo', { to });
  if (from) return t('audit.phrase.movedFrom', { from });

  return t('audit.phrase.moved');
}

/** i18n key per audit action. Absent means "no copy yet", handled above. */
export const AUDIT_PHRASE_KEY: Record<string, string> = {
  CREATE: 'audit.phrase.created',
  IN_PROGRESS: 'audit.phrase.started',
  ON_HOLD: 'audit.phrase.held',
  HOLD: 'audit.phrase.held',
  RESUME: 'audit.phrase.resumed',
  RESOLVED: 'audit.phrase.completed',
  VERIFIED: 'audit.phrase.verified',
  CLOSED: 'audit.phrase.closed',
  ESCALATED: 'audit.phrase.escalated',
  CANCELLED: 'audit.phrase.cancelled',
  TRANSITION: 'audit.phrase.overridden',
  REPRIORITISE: 'audit.phrase.reprioritised',
  // ASSIGN is handled by `assignPhrase`, which needs the two names — one key cannot say
  // "moved from A to B", "moved to B" and "moved this job" at once.
};
